import {useMemo} from 'react';
import {getPermissionsForRoles} from '@common/roles';
import {useAuthStore} from '@common-web/stores/authStore';

/**
 * Client-side mirror of `UserAccess.permissions` on the API.
 *
 * The server resolves the effective set from the DB role→permission edges
 * in the Better Auth `customSession` plugin and ships it down as
 * `user.permissions`. We consume that array here.
 *
 * Transitional fallback: when `permissions` is missing (an older cookie /
 * replayed session from before the field existed), recompute from the code
 * role catalog so the UI isn't blanked while the user re-authenticates.
 * Logged so lingering old sessions are visible; the fallback is removed
 * once sessions have rolled over. The server is always authoritative —
 * this hook only drives client-side gating.
 */
let warnedLegacySession = false;

export function usePermissions(): ReadonlySet<string> {
  const user = useAuthStore((state) => state.user);
  return useMemo(() => {
    if (!user) return new Set<string>();
    // An explicitly resolved empty array is a valid answer ("no permissions")
    // and must not trigger the fallback — only a missing field does.
    if (user.permissions) {
      return new Set(user.permissions);
    }
    if (!warnedLegacySession) {
      warnedLegacySession = true;
      console.warn(
        '[usePermissions] session payload has no permissions; falling back to the static role catalog. Re-authenticate to pick up DB-backed permissions.',
      );
    }
    const groupRoles = user.groupProfiles?.map((gp) => gp.role) ?? [];
    return getPermissionsForRoles([
      user.cbsInternalProfile?.internalRole,
      user.clientProfile?.role,
      ...groupRoles,
    ]);
  }, [user]);
}

/**
 * Convenience hook returning whether the current user holds a specific
 * permission. Memoised through `usePermissions` underneath.
 */
export function useHasPermission(key: string): boolean {
  const permissions = usePermissions();
  return permissions.has(key);
}

/**
 * Client-portal gate combining "the viewer is a client (tenant) user" with a
 * permission check — the frontend mirror of the API's `hasClientPermission`.
 * Returns `false` for internal/broker/group viewers even when the key is in
 * their permission set.
 */
export function useHasClientProfilePermission(key: string): boolean {
  const hasClientProfile = useAuthStore((state) => !!state.user?.clientProfile);
  const hasPermission = useHasPermission(key);
  return hasClientProfile && hasPermission;
}

/**
 * Frontend mirror of the API's `assertClientActionPermission`: the check only
 * applies to client (tenant) users — internal, broker, and group viewers pass
 * unconditionally because their access is scoped by profile, not by these
 * client action keys.
 */
export function useClientActionAllowed(key: string): boolean {
  const hasClientProfile = useAuthStore((state) => !!state.user?.clientProfile);
  const hasPermission = useHasPermission(key);
  return !hasClientProfile || hasPermission;
}

/**
 * Portal-aware action gate. Internal portal users must hold the key;
 * everyone else keeps `useClientActionAllowed` (client-profile viewers are
 * checked; broker/group viewers pass because those keys are client-scoped).
 */
export function usePortalActionAllowed(key: string): boolean {
  const isInternal = useAuthStore((state) => state.isInternalUser());
  const hasPermission = useHasPermission(key);
  const clientAllowed = useClientActionAllowed(key);
  return isInternal ? hasPermission : clientAllowed;
}
